import { Link, useLocation } from 'react-router-dom'
import { useEffect, useState } from 'react'
import { ROUTES } from '@/constants/routes'
import {
  FiGrid,
  FiFolder,
  FiSettings,
  FiX,
  FiChevronUp,
  FiChevronDown,
  FiZap,
} from 'react-icons/fi'
import Logo from '@/components/Logo'
import { AUTH_USER_CHANGED_EVENT, getUser } from '@/utils/auth'
import { ROLE_LABELS, getAvatarUrl } from '@/services/api/users'
import type { UserRole } from '@/services/api/users'
import { hasPermission } from '@/constants/permissions'
import type { UserRole as PermissionUserRole } from '@/types'

interface SidebarProps {
  isOpen: boolean
  onClose: () => void
}

interface MenuItem {
  label: string
  path: string
  icon: React.ReactNode
  permission?: string
  children?: { label: string; path: string; permission?: string }[]
}

const Sidebar = ({ isOpen, onClose }: SidebarProps) => {
  const location = useLocation()
  const [expandedItems, setExpandedItems] = useState<string[]>(['Finance', 'Contract File'])
  const [currentUser, setCurrentUser] = useState(() => getUser())
  const [avatarLoadFailed, setAvatarLoadFailed] = useState(false)

  useEffect(() => {
    const syncUser = () => setCurrentUser(getUser())
    window.addEventListener('storage', syncUser)
    window.addEventListener(AUTH_USER_CHANGED_EVENT, syncUser)
    return () => {
      window.removeEventListener('storage', syncUser)
      window.removeEventListener(AUTH_USER_CHANGED_EVENT, syncUser)
    }
  }, [])

  useEffect(() => {
    // When avatar/user changes, allow image to attempt loading again.
    setAvatarLoadFailed(false)
  }, [currentUser?.avatar, (currentUser as any)?.updatedAt])
  const canAccess = (permission?: string) => {
    if (!permission) return true
    if (!currentUser?.role) return false
    return hasPermission(currentUser.role as PermissionUserRole, permission)
  }

  const menuItems: MenuItem[] = [
    { label: 'Dashboard', path: ROUTES.DASHBOARD, icon: <FiGrid className="w-5 h-5" />, permission: 'dashboard:view' },
    {
      label: 'Contract File',
      path: ROUTES.CONTRACTS_BUY,
      icon: <FiFolder className="w-5 h-5" />,
      permission: 'contracts:view',
      children: [
        { label: 'Buy Contract', path: ROUTES.CONTRACTS_BUY, permission: 'contracts:view' },
        { label: 'Sale Contract', path: ROUTES.CONTRACTS_SALE, permission: 'contracts:view' },
        { label: 'Contract Performance', path: ROUTES.CONTRACTS_PERFORMANCE, permission: 'contracts:view' },
      ],
    },
    { label: 'Shipments', path: ROUTES.SHIPMENTS, icon: <img src="/images/ship.svg" alt="Ship" className="w-5 h-5" />, permission: 'shipments:view' },
    { label: 'Clearing Status', path: ROUTES.CLEARING, icon: <img src="/images/clearing-status.svg" alt="Clearing Status" className="w-5 h-5" />, permission: 'clearing:view' },
    { label: 'Storage & Inventory', path: ROUTES.INVENTORY, icon: <img src="/images/shop.svg" alt="Shop" className="w-5 h-5" />, permission: 'inventory:view' },
    { label: 'Stock Dispatch', path: ROUTES.LOAD_OUT, icon: <img src="/images/dispach-loadOut.svg" alt="Dispatch & Load Out" className="w-5 h-5" />, permission: 'load_out:view' },
    { label: 'Order Fulfillment', path: ROUTES.ORDER_FULFILLMENT, icon: <img src="/images/order-fulfillment.svg" alt="Order Fulfillment" className="w-5 h-5" /> },
    {
      label: 'Finance',
      path: ROUTES.FINANCE_OVERVIEW,
      icon: <img src="/images/finance.svg" alt="Finance" className="w-5 h-5" />,
      permission: 'payments:view',
      children: [
        { label: 'Overview', path: ROUTES.FINANCE_OVERVIEW, permission: 'payments:view' },
        { label: 'Invoices', path: ROUTES.FINANCE_INVOICES, permission: 'payments:view' },
        { label: 'Purchases', path: ROUTES.FINANCE_PURCHASES, permission: 'payments:view' },
        { label: 'Products and Services', path: ROUTES.FINANCE_PRODUCTS_SERVICES, permission: 'payments:view' },
        { label: 'Operational Costs', path: ROUTES.FINANCE_OPERATIONAL_COSTS, permission: 'payments:view' },
        { label: 'Business Snapshot', path: ROUTES.FINANCE_SNAPSHOT, permission: 'payments:view' },
        { label: 'Business Analytics', path: ROUTES.FINANCE_ANALYTICS, permission: 'payments:view' },
      ],
    },
    { label: 'Reports', path: ROUTES.REPORTS, icon: <img src="/images/report.svg" alt="Reports" className="w-5 h-5" />, permission: 'reports:view' },
    { label: 'AI Actions', path: ROUTES.AI_ACTIONS, icon: <FiZap className="w-5 h-5" /> },
  ]

  const otherItems: MenuItem[] = [
    { label: 'Setting', path: ROUTES.SETTINGS, icon: <FiSettings className="w-5 h-5" /> },
  ]

  const isActive = (path: string) => {
    return location.pathname === path || location.pathname.startsWith(path + '/')
  }

  const isItemActive = (item: MenuItem) => {
    if (item.children) {
      // Check if any child is active
      return item.children.some((child) => isActive(child.path))
    }
    return isActive(item.path)
  }

  const toggleExpand = (label: string) => {
    setExpandedItems((prev) =>
      prev.includes(label) ? prev.filter((item) => item !== label) : [...prev, label]
    )
  }

  const isExpanded = (label: string) => expandedItems.includes(label)

  return (
    <>
      {/* Overlay for mobile */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
          onClick={onClose}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`
          fixed lg:fixed left-0 z-50
          bg-white shadow-lg transform transition-transform duration-300 ease-in-out
          ${isOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
          flex flex-col
        `}
        style={{ 
          width: '242px', 
          top: '92px',
          bottom: '0',
          height: 'calc(100vh - 92px)'
        }}
      >
        {/* Close button for mobile */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200 lg:hidden">
          <Logo size="sm" />
          <button
            onClick={onClose}
            className="p-2 rounded-md text-gray-600 hover:bg-gray-100"
            aria-label="Close menu"
          >
            <FiX className="w-5 h-5" />
          </button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto" style={{ paddingTop: '24px' }}>
          {/* MENU Section */}
          <div className="px-4 mb-2">
            <p 
              className="uppercase tracking-wider"
              style={{
                fontFamily: 'Lexend Deca',
                fontWeight: 400,
                fontSize: '12px',
                lineHeight: '16px',
                color: '#212126',
                opacity: 0.6
              }}
            >
              MENU
            </p>
          </div>
          <ul className="space-y-1 px-2">
            {menuItems.map((item) => (
              <li key={item.path}>
                {item.children ? (
                  <>
                    {(() => {
                      const itemAccessible = canAccess(item.permission)
                      return (
                    <button
                      onClick={() => toggleExpand(item.label)}
                      className={`
                        w-full flex items-center justify-between gap-3 px-4 py-3 rounded-lg transition-colors
                        ${
                          isItemActive(item)
                            ? 'bg-gray-100 text-gray-900 font-medium'
                            : itemAccessible
                              ? 'text-gray-700 hover:bg-gray-50'
                              : 'text-gray-500 hover:bg-gray-50'
                        }
                      `}
                    >
                      <div className="flex items-center gap-3">
                        {item.icon}
                        <span>{item.label}</span>
                      </div>
                      {isExpanded(item.label) ? (
                        <FiChevronUp className="w-4 h-4" />
                      ) : (
                        <FiChevronDown className="w-4 h-4" />
                      )}
                    </button>
                      )
                    })()}
                    {isExpanded(item.label) && (
                      <ul className="ml-4 mt-1 space-y-1">
                        {item.children.map((child) => (
                          <li key={child.path} className="relative">
                            {(() => {
                              const childAccessible = canAccess(child.permission)
                              return (
                            <Link
                              to={child.path}
                              onClick={() => {
                                if (window.innerWidth < 1024) {
                                  onClose()
                                }
                              }}
                              className={`
                                flex items-center gap-3 px-4 py-2 rounded-lg transition-colors
                                ${
                                  isActive(child.path)
                                    ? 'bg-gray-100 text-gray-900 font-medium'
                                    : childAccessible
                                      ? 'text-gray-600 hover:bg-gray-50'
                                      : 'text-gray-400 hover:bg-gray-50'
                                }
                              `}
                            >
                              <span className="text-sm">{child.label}</span>
                            </Link>
                              )
                            })()}
                            {/* Active Indicator Bar for child items */}
                            {isActive(child.path) && (
                              <div
                                className="absolute right-0 top-1/2 transform -translate-y-1/2"
                                style={{
                                  width: '3px',
                                  height: '40px',
                                  backgroundColor: '#212126',
                                  borderTopRightRadius: '20px',
                                  borderBottomRightRadius: '20px',
                                }}
                              />
                            )}
                          </li>
                        ))}
                      </ul>
                    )}
                  </>
                ) : (
                  <div className="relative">
                    {(() => {
                      const itemAccessible = canAccess(item.permission)
                      return (
                    <Link
                      to={item.path}
                      onClick={() => {
                        if (window.innerWidth < 1024) {
                          onClose()
                        }
                      }}
                      className={`
                        flex items-center gap-3 px-4 py-3 rounded-lg transition-colors
                        ${
                          isActive(item.path)
                            ? 'bg-gray-100 text-gray-900 font-medium'
                            : itemAccessible
                              ? 'text-gray-700 hover:bg-gray-50'
                              : 'text-gray-500 hover:bg-gray-50'
                        }
                      `}
                    >
                      {item.icon}
                      <span>{item.label}</span>
                    </Link>
                      )
                    })()}
                    {/* Active Indicator Bar */}
                    {isActive(item.path) && (
                      <div
                        className="absolute right-0 top-1/2 transform -translate-y-1/2"
                        style={{
                          width: '3px',
                          height: '40px',
                          backgroundColor: '#212126',
                          borderTopRightRadius: '20px',
                          borderBottomRightRadius: '20px',
                        }}
                      />
                    )}
                  </div>
                )}
              </li>
            ))}
          </ul>

          {/* OTHERS Section */}
          <div className="px-4 mt-6 mb-2">
            <p className="text-xs font-medium text-gray-400 uppercase tracking-wider">OTHERS</p>
          </div>
          <ul className="space-y-1 px-2">
            {otherItems.map((item) => (
              <li key={item.path}>
                <Link
                  to={item.path}
                  onClick={() => {
                    if (window.innerWidth < 1024) {
                      onClose()
                    }
                  }}
                  className={`
                    flex items-center gap-3 px-4 py-3 rounded-lg transition-colors
                    ${
                      isActive(item.path)
                        ? 'bg-gray-100 text-gray-900 font-medium'
                        : 'text-gray-700 hover:bg-gray-50'
                    }
                  `}
                >
                  {item.icon}
                  <span>{item.label}</span>
                </Link>
              </li>
            ))}
          </ul>
        </nav>

        {/* User Profile Section - Bottom */}
        <div className="mt-auto border-t border-gray-200 p-4">
          <div className="flex items-center gap-3">
            {(() => {
              const initials = currentUser
                ? (currentUser.name || currentUser.email || '')
                    .split(/\s+/)
                    .map((s) => s[0])
                    .join('')
                    .toUpperCase()
                    .slice(0, 2) || '?'
                : '?'
              const displayName = currentUser?.name || currentUser?.email || 'Guest'
              const displayRole = currentUser?.role ? (ROLE_LABELS[currentUser.role as UserRole] || currentUser.role) : ''
              return (
                <>
                  <div className="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center flex-shrink-0 overflow-hidden">
                    {(() => {
                      const rawSrc = getAvatarUrl(currentUser?.avatar ?? null)
                      const updatedAt = (currentUser as any)?.updatedAt as string | undefined
                      const isSignedUrl = !!rawSrc && /[?&]X-Amz-(Signature|Algorithm)=/i.test(rawSrc)
                      const src = rawSrc && updatedAt && !isSignedUrl
                        ? `${rawSrc}${rawSrc.includes('?') ? '&' : '?'}v=${encodeURIComponent(updatedAt)}`
                        : rawSrc
                      return src && !avatarLoadFailed ? (
                        <img
                          src={src}
                          alt={displayName}
                          className="w-full h-full object-cover"
                          onError={() => setAvatarLoadFailed(true)}
                        />
                      ) : (
                        <span className="text-sm font-medium text-gray-600">{initials}</span>
                      )
                    })()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p
                      className="truncate"
                      style={{
                        fontFamily: 'Lexend Deca',
                        fontWeight: 400,
                        fontSize: '14px',
                        lineHeight: '20px',
                        color: '#212126',
                      }}
                    >
                      {displayName}
                    </p>
                    <p
                      className="truncate"
                      style={{
                        fontFamily: 'Lexend Deca',
                        fontWeight: 400,
                        fontSize: '14px',
                        lineHeight: '20px',
                        color: '#69697A',
                      }}
                    >
                      {displayRole}
                    </p>
                  </div>
                </>
              )
            })()}
          </div>
        </div>
      </aside>
    </>
  )
}

export default Sidebar

